import {
    Directive,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnDestroy,
    OnInit,
    Output,
} from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { debounceTime, filter, tap } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy()
/**
 * Emits events when the host scrolls near top or bottom and reports scrollTop.
 *
 * Inputs:
 * - `debouceTime = 100` — Debounce time for emitted events (ms).
 * - `bottomPercentage = 0.95` — Threshold relative to viewport height for bottom.
 * - `topPercentage = 0.05` — Threshold relative to viewport height for top.
 *
 * Outputs:
 * - `scrollToBottom` — Emits true when approaching bottom threshold.
 * - `scrollToTop` — Emits true when approaching top threshold.
 * - `scrollTopPosition` — Current `scrollTop` value (debounced).
 *
 * Example:
 * ```html
 * <div scroll style="overflow:auto;height:400px"
 *      (scrollToBottom)="loadMore()"
 *      (scrollToTop)="reachedTop()"
 *      (scrollTopPosition)="savePos($event)">
 *   ...
 * </div>
 * ```
 */
@Directive({
    selector: '[scroll]',
    standalone: true,
})
export class ScrollDirective implements OnDestroy, OnInit {
    @Input()
    /**
     * Debounce time (ms) for emitting scroll-related outputs.
     */
    debouceTime = 100;

    @Input()
    /**
     * Threshold relative to viewport height to consider as near bottom.
     */
    bottomPercentage = 0.95;

    @Input()
    /**
     * Threshold relative to viewport height to consider as near top.
     */
    topPercentage = 0.05;

    @Output()
    /**
     * Emits true when scrolled near bottom threshold.
     */
    public scrollToBottom = new EventEmitter<boolean>();

    @Output()
    /**
     * Emits true when scrolled near top threshold.
     */
    public scrollToTop = new EventEmitter<boolean>();

    @Output()
    /**
     * Emits the current scrollTop position (debounced).
     */
    public scrollTopPosition = new EventEmitter<number>();

    onScrollBottom$: Subject<boolean> = new Subject();

    onScrollTop$: Subject<boolean> = new Subject();

    scrollPosition$: Subject<number> = new Subject();

    protected _prevScrollTop;

    @HostListener('scroll', ['$event'])
    /**
     * Host scroll listener that computes thresholds and direction.
     */
    private onScroll($event: Event) {
        const el = this.el.nativeElement;
        const top = Math.abs(el.scrollTop);
        const direction = top > this._prevScrollTop ? 'bottom' : 'top';
        this._prevScrollTop = top;
        if (el.scrollHeight <= el.offsetHeight) return;

        this.scrollPosition$.next(top);

        if (
            direction === 'bottom' &&
            el.scrollHeight - top - el.offsetHeight < el.offsetHeight * (1 - this.bottomPercentage)
        ) {
            this.onScrollBottom$.next(true);
            return;
        }
        if (direction === 'top' && top < el.offsetHeight * this.topPercentage) {
            this.onScrollTop$.next(true);
        }
    }

    constructor(private el: ElementRef) {}

    ngOnInit() {
        this.onScrollBottom$
            .pipe(
                debounceTime(100),
                tap(() => {
                    this.scrollToBottom.emit(true);
                }),
                untilDestroyed(this),
            )
            .subscribe();

        this.onScrollTop$
            .pipe(
                debounceTime(100),
                tap(() => {
                    this.scrollToTop.emit(true);
                }),
                untilDestroyed(this),
            )
            .subscribe();

        this.scrollPosition$
            .pipe(
                debounceTime(100),
                tap((v) => {
                    this.scrollTopPosition.emit(v);
                }),
                untilDestroyed(this),
            )
            .subscribe();
    }

    ngOnDestroy() {}
}
